import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Col,
  Divider,
  Popconfirm,
  Row,
  Table,
  TableProps,
  Tag,
} from "antd";
import { CSVLink } from "react-csv";

import {
  DeleteOutlined,
  EditOutlined,
  ExportOutlined,
  EyeOutlined,
  PlusOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import {
  ColumnsType,
  ColumnType as AntdColumnType,
  CompareFn,
  TableRowSelection,
  FilterValue,
  SorterResult,
} from "antd/lib/table/interface";
import formatBoolean from "helpers/formatBoolean";
import formatNumber from "helpers/formatNumber";
import sortAlphabetical from "helpers/sortAlphabetical";
import sortBoolean from "helpers/sortBoolean";
import sortNumber from "helpers/sortNumber";

import { Params, Value } from "types/global.types";
import { useTranslation } from "react-i18next";
import useSearchColumn from "hooks/useSearchColumn";

import styles from "./DataTable.module.less";

export enum ColumnType {
  Number = "NUMBER",
  Text = "TEXT",
  Boolean = "BOOL",
}

export enum ActionType {
  View = "VIEW",
  Edit = "EDIT",
  Delete = "DELETE",
  Other = "OTHER",
  Add = "ADD",
  Refresh = "REFRESH",
}

export type FiltersValues = {
  text: string;
  value: string | number | boolean;
}[];

interface Header<T extends Params> {
  key: keyof T;
  title: string;
  type: ColumnType;
  customFormater?(value: T[keyof T], data: T): JSX.Element;
  filters?: FiltersValues;
  width?: number | string;
  onFilter?: (value: string | number | boolean, record: T) => boolean;
  withSearchFilter?: boolean;
  onCell?(
    data: T,
    index?: number
  ): React.HTMLAttributes<T> | React.TdHTMLAttributes<T>;
}

interface Action<T> {
  type: ActionType;
  title?: string;
  width?: number | string;
  action({
    row,
    selectedRowKeys,
    setSelectedRowKeys,
    filteredRowKeys,
  }: {
    row?: T;
    selectedRowKeys?: string[];
    setSelectedRowKeys?: React.Dispatch<React.SetStateAction<string[]>>;
    filteredRowKeys?: string[];
  }): void;
  isDisabled?({
    row,
    selectedRowKeys,
  }: {
    row?: T;
    selectedRowKeys?: string[];
  }): boolean;
  icon?: JSX.Element;
  text?: string;
  confirmationText?: string;
  loading?: boolean;
}

interface Props<T extends Params> {
  headers: Header<T>[];
  actions: Action<T>[];
  data?: T[];
  isLoading: boolean;
  title?: React.ReactNode;
  stickyHeader?: boolean;
  disablePagination?: boolean;
  defaultPageSize?: number;
  rowSelection?: TableRowSelection<T>;
  withTitle?: boolean;
  isHorizontalScrollDisabled?: boolean;
  clearTrigger?: number;
}

declare type FixedType = "left" | "right" | boolean;

function getSorterFunction(type: ColumnType): CompareFn<Value> {
  if (type === ColumnType.Number) {
    return (a: Value, b: Value) => sortNumber(a as number, b as number);
  }

  if (type === ColumnType.Boolean) {
    return (a: Value, b: Value) => sortBoolean(a as boolean, b as boolean);
  }

  return (a: Value, b: Value) => sortAlphabetical(a as string, b as string);
}

function getRenderFunction(
  type: ColumnType
): (value: Value) => Value | JSX.Element {
  if (type === ColumnType.Number) {
    return (value: Value) => {
      if (typeof value === "number") {
        return formatNumber(value);
      }
      return "";
    };
  }

  if (type === ColumnType.Boolean) {
    return (value: Value) => {
      if (typeof value === "boolean") {
        const renderBoolean = (val: boolean) => (
          <Tag color={val ? "success" : "error"}>{formatBoolean(val)}</Tag>
        );
        return renderBoolean(value);
      }
      return "";
    };
  }

  return function render(value: Value) {
    return <span>{value}</span>;
  };
}

function generateHeaders<T extends Params>({
  headers,
  getColumnSearchProps,
  booleanFilters,
  filteredInfo,
  sortedInfo,
}: {
  headers: Header<T>[];
  getColumnSearchProps: (dataIndex: keyof T) => AntdColumnType<T>;
  booleanFilters: FiltersValues;
  filteredInfo: Record<string, FilterValue | null>;
  sortedInfo: SorterResult<T>;
}): ColumnsType<T> {
  return headers.map(
    ({
      key,
      title,
      type,
      filters,
      width,
      customFormater,
      onFilter,
      withSearchFilter,
      onCell,
    }) => {
      const sort = getSorterFunction(type);
      const render = customFormater || getRenderFunction(type);
      return {
        key: String(key),
        title,
        dataIndex: String(key),
        sortOrder: sortedInfo.columnKey === key ? sortedInfo.order : null,
        sorter: (a: T, b: T) => sort(a[key], b[key]),
        render: (_, record: T) => render(record[key], record),
        filters: type === ColumnType.Boolean ? booleanFilters : filters,
        filterSearch: true,
        filteredValue: filteredInfo[String(key)] || null,
        onFilter:
          type === ColumnType.Boolean
            ? (value, record) => value === record[key]
            : onFilter,
        width,
        ...(withSearchFilter && getColumnSearchProps(key)),
        onCell,
        className: onCell ? styles.clickable_cell : "",
      };
    }
  );
}

const icons = {
  [ActionType.Delete]: <DeleteOutlined />,
  [ActionType.View]: <EyeOutlined />,
  [ActionType.Edit]: <EditOutlined />,
  [ActionType.Add]: <PlusOutlined />,
  [ActionType.Refresh]: <SyncOutlined />,
};

const renderers: {
  [key: string]: (props: {
    onClick: () => void;
    icon: React.ReactNode;
    isDisabled: boolean;
    loading: boolean;
    confirmationText: string;
  }) => JSX.Element;
} = {
  [ActionType.Delete]: ({
    onClick,
    isDisabled,
    loading,
    confirmationText,
  }: {
    onClick: () => void;
    isDisabled: boolean;
    loading: boolean;
    confirmationText: string;
  }) => (
    <Popconfirm
      title={confirmationText}
      onConfirm={onClick}
      okText="Yes"
      cancelText="No"
      disabled={isDisabled}
    >
      <Button
        type="primary"
        disabled={isDisabled}
        shape="circle"
        icon={icons[ActionType.Delete]}
        loading={loading}
      />
    </Popconfirm>
  ),
  [ActionType.View]: ({
    onClick,
    isDisabled,
    loading,
  }: {
    onClick: () => void;
    isDisabled: boolean;
    loading: boolean;
  }) => (
    <Button
      shape="circle"
      type="primary"
      size="large"
      icon={icons[ActionType.View]}
      onClick={onClick}
      disabled={isDisabled}
      loading={loading}
    />
  ),
  [ActionType.Edit]: ({
    onClick,
    isDisabled,
    loading,
  }: {
    onClick: () => void;
    isDisabled: boolean;
    loading: boolean;
  }) => (
    <Button
      shape="circle"
      type="primary"
      size="large"
      icon={icons[ActionType.Edit]}
      onClick={onClick}
      disabled={isDisabled}
      loading={loading}
    />
  ),
  [ActionType.Other]: ({
    onClick,
    icon,
    isDisabled,
    loading,
  }: {
    onClick: () => void;
    icon: React.ReactNode;
    isDisabled: boolean;
    loading: boolean;
  }) => (
    <Button
      shape="circle"
      type="primary"
      size="large"
      icon={icon}
      onClick={onClick}
      disabled={isDisabled}
      loading={loading}
    />
  ),
  [ActionType.Refresh]: ({
    onClick,
    icon,
    isDisabled,
    loading,
  }: {
    onClick: () => void;
    icon: React.ReactNode;
    isDisabled: boolean;
    loading: boolean;
  }) => (
    <Button
      shape="circle"
      type="primary"
      size="large"
      icon={icon}
      onClick={onClick}
      disabled={isDisabled}
      loading={loading}
    />
  ),
};

function generateActions<T>({
  actions,
}: {
  actions: Action<T>[];
}): ColumnsType<T> {
  const generatedActions = actions?.map(
    ({
      type,
      title,
      width,
      action,
      isDisabled,
      icon,
      confirmationText,
      loading,
    }) => {
      const render = renderers[type];

      return {
        key: type,
        title: title || "",
        width,
        render: (_: string | number | boolean, row: T) =>
          render({
            onClick: () => action({ row }),
            isDisabled: isDisabled?.({ row }) || false,
            icon,
            confirmationText: confirmationText || "",
            loading: loading || false,
          }),
        fixed: "right" as FixedType,
      };
    }
  );

  return generatedActions;
}

function DataTable<
  T extends {
    id: number | string;
  }
>({
  data,
  headers,
  actions,
  isLoading,
  title,
  stickyHeader,
  disablePagination,
  defaultPageSize,
  rowSelection,
  withTitle = true,
  isHorizontalScrollDisabled = false,
  clearTrigger,
}: Props<T>) {
  const { t } = useTranslation();
  const getColumnSearchProps = useSearchColumn<T>();
  const booleanFilters = [
    { text: t("yes"), value: true },
    { text: t("no"), value: false },
  ];

  const [filteredInfo, setFilteredInfo] = useState<
    Record<string, FilterValue | null>
  >({});
  const [sortedInfo, setSortedInfo] = useState<SorterResult<T>>({});

  const renderHeaders = useMemo(
    () =>
      generateHeaders({
        headers,
        getColumnSearchProps,
        booleanFilters,
        filteredInfo,
        sortedInfo,
      }),
    [headers, filteredInfo, sortedInfo]
  );
  const renderActions = useMemo(() => generateActions({ actions }), [actions]);

  const handleChange: TableProps<T>["onChange"] = (
    _pagination,
    filters,
    sorter
  ) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter as SorterResult<T>);
  };

  useEffect(() => {
    if (clearTrigger) {
      setFilteredInfo({});
      setSortedInfo({});
    }
  }, [clearTrigger]);

  return (
    <>
      {withTitle ? (
        <Row justify="space-between" gutter={[20, 20]} wrap>
          <Col span={19}>{title}</Col>
          {data?.length ? (
            <Col>
              <CSVLink data={data} filename="data.csv" separator=";">
                <Button type="dashed" icon={<ExportOutlined />} block>
                  {t("export")}
                </Button>
              </CSVLink>
            </Col>
          ) : null}
        </Row>
      ) : null}
      <Divider />
      <div>
        <Table<T>
          loading={isLoading}
          dataSource={data}
          rowKey={(item) => item.id}
          pagination={
            disablePagination
              ? false
              : {
                  defaultPageSize: defaultPageSize || 50,
                  showSizeChanger: true,
                }
          }
          columns={[...renderHeaders, ...renderActions]}
          bordered
          scroll={isHorizontalScrollDisabled ? { x: 0 } : { x: "max-content" }}
          sticky={stickyHeader}
          rowSelection={rowSelection}
          onChange={handleChange}
        />
      </div>
    </>
  );
}

export default DataTable;
